import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import VideoPlayer from "./components/VideoPlayer";
import { formatDate, formatPlays, getRelatedVideos, getVideoById } from "@/utils/video";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function DetailPage() {
  const { videoId = "" } = useParams();
  const video = getVideoById(videoId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  useDocumentMeta(
    video ? `${video.title} - 西瓜视频视频详情` : "视频详情 - 西瓜视频",
    video
      ? `${video.description.slice(0, 90)} —— 西瓜视频「${video.categoryName}」频道高清在线观看。`
      : "西瓜视频视频详情页。",
    video
      ? `${video.title},${video.categoryName},西瓜视频,在线观看,${video.tags.join(",")}`
      : "西瓜视频,视频详情"
  );

  if (!video) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-film-line text-3xl text-foreground-400"></i>
        </span>
        <h1 className="text-lg font-bold text-foreground-900 mb-2">视频不存在</h1>
        <p className="text-sm text-foreground-500 mb-6">你访问的视频可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const fav = isFavorite(video.id);
  const related = getRelatedVideos(video, 6);

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // clipboard may be unavailable; still show feedback
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5 flex-wrap" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <Link
          to={`/category/${video.categoryId}`}
          className="hover:text-primary-600 transition-colors cursor-pointer"
        >
          {video.categoryName}
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="text-foreground-800 line-clamp-1">{video.title}</span>
      </nav>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <VideoPlayer cover={video.cover} title={video.title} duration={video.duration} />

          <h1 className="mt-5 text-xl md:text-2xl font-bold font-heading text-foreground-900 leading-snug">
            {video.title}
          </h1>

          <div className="mt-3 flex items-center gap-x-4 gap-y-2 flex-wrap text-sm text-foreground-500">
            <Link
              to={`/category/${video.categoryId}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer"
            >
              <i className="ri-price-tag-3-line"></i>
              {video.categoryName}
            </Link>
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {formatDate(video.publishDate)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line"></i>
              时长 {video.duration}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-play-circle-line"></i>
              {formatPlays(video.plays)}次播放
            </span>
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => toggleFavorite(video.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                fav
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:text-primary-600 border border-background-200"
              }`}
            >
              <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
              {fav ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-background-100 text-foreground-700 hover:text-primary-600 border border-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={copied ? "ri-check-line" : "ri-share-forward-line"}></i>
              {copied ? "链接已复制" : "分享"}
            </button>
          </div>

          <div className="mt-6 rounded-lg bg-background-100 border border-background-200 p-5">
            <h2 className="text-base font-bold text-foreground-900 mb-2">视频简介</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
            <div className="mt-4 pt-4 border-t border-background-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-foreground-400 shrink-0">类型</span>
                <span className="text-foreground-800">{video.categoryName} · {video.subcategory}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-foreground-400 shrink-0">地区</span>
                <span className="text-foreground-800">{video.region}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-foreground-400 shrink-0">年份</span>
                <span className="text-foreground-800">{video.year}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-foreground-400 shrink-0">主创</span>
                <span className="text-foreground-800">{video.cast}</span>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <aside className="w-full lg:w-[360px] shrink-0">
          <h2 className="text-base font-bold text-foreground-900 mb-4">相关推荐</h2>
          <div className="flex flex-col gap-4">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex gap-3 cursor-pointer"
              >
                <div className="relative w-40 shrink-0 aspect-video rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 西瓜视频`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute bottom-1.5 right-1.5 px-1 py-0.5 rounded bg-foreground-950/75 text-background-50 text-[10px]">
                    {item.duration}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1.5 flex items-center gap-1">
                    <i className="ri-play-circle-line"></i>
                    {formatPlays(item.plays)}次播放
                  </p>
                  <p className="text-xs text-foreground-400 mt-0.5">{item.categoryName}</p>
                </div>
              </Link>
            ))}
          </div>

          <Link
            to={`/category/${video.categoryId}`}
            className="mt-6 flex items-center justify-center gap-1 w-full py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            进入「{video.categoryName}」频道
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </aside>
      </div>
    </div>
  );
}